import { useState } from 'react';
import type { DentalOfficeShiftCardMenuItem } from '@tempmee/react-blocks';
import { IDashboardJob } from '~/domain/DashboardJobs';
import { useShiftCardCancel } from '~/components/shared/useShiftCardCancel';
import {
  fireShiftsOverviewInviteClicked,
  type IShiftsOverviewInviteClickedPayload,
} from '~/posthogs/events';
import type { TShiftCardData } from '~/types/ShiftCard';
import { mapDashboardJobToShiftCard } from './mapDashboardJobToShiftCard';

export interface IInviteConfig {
  /** PostHog `shift_status` on the remapped invite event. */
  shiftStatus: IShiftsOverviewInviteClickedPayload['shiftStatus'];
  /** Tracking source label forwarded to the invite popup. */
  trackingSource: 'Open Shift' | 'Action Needed';
}

export interface IShiftOverviewCardProps {
  item: IDashboardJob;
  status: TShiftCardData['status'];
  onCardClick: (item: IDashboardJob) => void;
  /** When set (and the shift is not an interview), wires up the Invite action. */
  invite?: IInviteConfig;
  /**
   * Where the Invite action lives: the block's envelope icon (`'envelope'`, default) or an
   * "Invite Professionals ([N] invited)" item in the `⋯` menu (`'menu'`, e.g. confirm
   * candidates / confirm hours per GOTU-4588). Ignored when `invite` is not set.
   */
  invitePlacement?: 'envelope' | 'menu';
  /** When set, adds a "Message professional" overflow item that runs this handler. */
  onChat?: (item: IDashboardJob) => void;
  /** When set, adds an "Edit Shift/Interview" overflow item that runs this handler. */
  onEdit?: (item: IDashboardJob) => void;
  /** When true, adds a "Cancel Shift/Interview" overflow item. */
  canCancel?: boolean;
}

/**
 * Drives a `ShiftOverviewCard`: maps the job to the block props, assembles the per-status
 * overflow menu (invite / chat / edit / cancel), and owns the invite popup state. The
 * cancel popup comes from `useShiftCardCancel`. Keeps the component itself presentational.
 */
export const useShiftOverviewCard = ({
  item,
  status,
  invite,
  invitePlacement = 'envelope',
  onChat,
  onEdit,
  canCancel = false,
}: IShiftOverviewCardProps) => {
  const [isInvitePopupOpen, setInvitePopupOpen] = useState(false);

  const isInterview = Boolean(item.dashboardJobApi?.isOnsiteInterview);
  const { openCancelPopup, cancelPopup } = useShiftCardCancel({
    jobId: item.id,
    status,
    isInterview,
    date: item.date,
  });
  const cardData = mapDashboardJobToShiftCard(item, status);
  const invitedCount = item.dashboardJobApi?.favoriteInvites?.length ?? 0;

  const openInvite =
    invite && !isInterview
      ? () => {
          fireShiftsOverviewInviteClicked({ shiftStatus: invite.shiftStatus, shiftId: item.id });
          setInvitePopupOpen(true);
        }
      : undefined;

  const menuItems: DentalOfficeShiftCardMenuItem[] = [];
  if (openInvite && invitePlacement === 'menu') {
    menuItems.push({
      label:
        invitedCount > 0 ? `Invite Professionals (${invitedCount} invited)` : 'Invite Professionals',
      onSelect: openInvite,
    });
  }
  if (onChat) {
    menuItems.push({ label: 'Message professional', onSelect: () => onChat(item) });
  }
  if (onEdit) {
    menuItems.push({
      label: isInterview ? 'Edit Interview' : 'Edit Shift',
      onSelect: () => onEdit(item),
    });
  }
  if (canCancel) {
    menuItems.push({
      label: isInterview ? 'Cancel Interview' : 'Cancel Shift',
      onSelect: openCancelPopup,
    });
  }

  return {
    cardData,
    isInterview,
    // Invite lives either on the block's envelope icon or as a `⋯` menu item.
    handleInvite: openInvite && invitePlacement === 'envelope' ? openInvite : undefined,
    menuItems: menuItems.length > 0 ? menuItems : undefined,
    isInvitePopupOpen,
    closeInvitePopup: () => setInvitePopupOpen(false),
    cancelPopup,
  };
};
